import React from 'react';
import ContactCTA from '../components/ContactCTA';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8 text-center">Privacy Policy</h1>
      
      <div className="prose max-w-none">
        <p className="mb-4">Last updated: May 20, 2025</p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Introduction</h2>
        <p className="mb-4">
          VernPOS ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
          use, disclose, and safeguard your information when you use our point-of-sale system, mobile application, 
          and website (collectively, the "Service").
        </p>
        <p className="mb-4">
          Please read this Privacy Policy carefully. By accessing or using our Service, you acknowledge that you have read, 
          understood, and agree to be bound by all the terms outlined in this Privacy Policy.
        </p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Information We Collect</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">Personal Information</h3>
        <p className="mb-4">
          We may collect personal information that you voluntarily provide when using our Service, including:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Contact information (name, email address, phone number, business address)</li>
          <li>Account credentials</li>
          <li>Business information (business name, tax ID, industry type)</li>
          <li>Payment information (processed through secure third-party payment processors)</li>
          <li>Communication preferences</li>
        </ul>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">Transaction Information</h3>
        <p className="mb-4">
          When you use our POS system, we collect transaction data including:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Purchase history</li>
          <li>Sales data</li>
          <li>Inventory information</li>
          <li>Customer data (if you choose to collect it)</li>
        </ul>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">Usage Information</h3>
        <p className="mb-4">
          We automatically collect certain information about how you interact with our Service, including:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li>Log data (IP address, browser type, pages visited, time spent)</li>
          <li>Device information (hardware model, operating system)</li>
          <li>Location information (with your consent)</li>
          <li>Cookies and similar tracking technologies</li>
        </ul>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">How We Use Your Information</h2>
        <p className="mb-4">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Provide, maintain, and improve our Service</li>
          <li>Process transactions and send related information</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Send you technical notices, updates, security alerts, and support messages</li>
          <li>Develop new products and services</li>
          <li>Generate anonymized, aggregate data for internal analysis</li>
          <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
          <li>Comply with legal obligations</li>
        </ul>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Information Sharing and Disclosure</h2>
        <p className="mb-4">We may share your information with:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Service providers who perform services on our behalf</li>
          <li>Payment processors to complete transactions</li>
          <li>Business partners with your consent</li>
          <li>Legal authorities when required by law or to protect our rights</li>
          <li>In connection with a business transaction (merger, acquisition, or sale)</li>
        </ul>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Data Security</h2>
        <p className="mb-4">
          We implement appropriate technical and organizational measures to protect the security of your personal information. 
          However, no electronic transmission or storage technology is completely secure. While we strive to protect your 
          information, we cannot guarantee its absolute security.
        </p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Your Rights and Choices</h2>
        <p className="mb-4">Depending on your location, you may have certain rights regarding your personal information, including:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Access to your personal information</li>
          <li>Correction of inaccurate or incomplete information</li>
          <li>Deletion of your personal information</li>
          <li>Restriction of processing</li>
          <li>Data portability</li>
          <li>Objection to processing</li>
        </ul>
        <p className="mb-4">
          To exercise these rights, please contact us using the information provided in the "Contact Us" section.
        </p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Retention of Information</h2>
        <p className="mb-4">
          We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
          unless a longer retention period is required or permitted by law.
        </p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">International Data Transfers</h2>
        <p className="mb-4">
          Your information may be transferred to, and maintained on, computers located outside of your state, province, country, 
          or other governmental jurisdiction where the data protection laws may differ. If you are located outside Malaysia and 
          choose to provide information to us, please note that we transfer the information to Malaysia and process it there.
        </p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Changes to This Privacy Policy</h2>
        <p className="mb-4">
          We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last updated" date. 
          We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
        </p>
        
        <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h2>
        <p className="mb-4">
          If you have any questions about this Privacy Policy, please contact us at:
        </p>
        <p className="mb-4">
          <strong>Email:</strong> info@vernpos.my<br />
          <strong>Phone:</strong> +60 1700 81 6729<br />
          <strong>Address:</strong> Lot 109-113, Jalan USJ 21/10, 47630 Subang Jaya, Selangor Darul Ehsan, Malaysia
        </p>
      </div>
      
      {/* CTA Banner */}
      <div className="mt-16">
        <ContactCTA />
      </div>
    </div>
  );
};

export default PrivacyPolicy;
